/**
 * CCP & Calls Logger Utility
 * 
 * Hybrid logging approach:
 * - Milestones → Sentry.logger.* (searchable in Sentry Logs dashboard)
 * - Verbose context → Sentry.addBreadcrumb (visible only when an error occurs)
 * - Errors → Sentry.logger.error (always logged)
 * - Console output → only in development mode
 * 
 * Usage:
 *   ccpLogger.info('CCP Initialized', { agentName: 'John' })
 *   callLogger.error('Call failed', { contactId: '123' })
 *   callLogger.breadcrumb('Call state changed', { state: 'CONNECTED' })
 * 
 * Filter in Sentry Logs dashboard:
 *   module:ccp          → CCP init/auth logs
 *   module:calls        → Call lifecycle logs
 *   module:diagnostics  → Connect endpoint latency / network diagnostics
 */
import * as Sentry from '@sentry/react'

const isDev = process.env.NODE_ENV === 'development'

// Shared context store for enriching logs with user/call identifiers
let _context: {
  userId?: string
  agentName?: string
  agentEmail?: string
  contactId?: string
  callHeaderId?: string
} = {}

/**
 * Set shared context so all subsequent logs include these identifiers.
 * Call this when agent initializes or a new contact arrives.
 */
export const setLogContext = (ctx: Partial<typeof _context>) => {
  _context = { ..._context, ...ctx }
}

/**
 * Clear call-specific context (contactId, callHeaderId).
 * Call this when a call ends or contact is destroyed.
 */
export const clearCallContext = () => {
  delete _context.contactId
  delete _context.callHeaderId
}

// Build enriched data by merging shared context with per-call data
const enrich = (data?: Record<string, any>): Record<string, any> => {
  const enriched: Record<string, any> = {}
  // Only include context fields that have values
  if (_context.userId) enriched.userId = _context.userId
  if (_context.agentName) enriched.agentName = _context.agentName
  if (_context.agentEmail) enriched.agentEmail = _context.agentEmail
  if (_context.contactId) enriched.contactId = _context.contactId
  if (_context.callHeaderId) enriched.callHeaderId = _context.callHeaderId
  return { ...enriched, ...data }
}

const createLogger = (module: 'ccp' | 'calls' | 'token' | 'diagnostics') => ({
  /**
   * Milestone log — searchable in Sentry Logs dashboard.
   * Use for key events: agent init, call connected, call ended, etc.
   */
  info: (message: string, data?: Record<string, any>) => {
    const enrichedData = enrich(data)
    if (isDev) console.log(`[ConnectStreams]`, message, enrichedData)
    Sentry.logger.info(message, { module, ...enrichedData })
  },

  /**
   * Warning — logged to Sentry Logs with warn level.
   * Use for: auth failures, timeouts, non-critical issues.
   */
  warn: (message: string, data?: Record<string, any>) => {
    const enrichedData = enrich(data)
    if (isDev) console.warn(`[ConnectStreams]`, message, enrichedData)
    Sentry.logger.warn(message, { module, ...enrichedData })
  },

  /**
   * Error — always logged to Sentry Logs.
   * For module:ccp, also captured as a Sentry Issue with a [CCP] message prefix.
   * Use for: failed SDK calls, exceptions, API failures.
   */
  error: (message: string, data?: Record<string, any>) => {
    const enrichedData = enrich(data)
    const prefixed =
      module === 'ccp'
        ? message.startsWith('[CCP]')
          ? message
          : `[CCP] ${message}`
        : message
    if (isDev) console.error(`[ConnectStreams]`, prefixed, enrichedData)
    Sentry.logger.error(prefixed, { module, ...enrichedData })
    if (module === 'ccp') {
      Sentry.withScope((scope) => {
        scope.setTag('module', module)
        scope.setTag('ccp.log_level', 'ERROR')
        scope.setContext('ccpLog', enrichedData)
        Sentry.captureMessage(prefixed, 'error')
      })
    }
  },

  /**
   * Breadcrumb — only visible when an error occurs (attached as context trail).
   * Use for: state transitions, queue operations, verbose debug info.
   * Zero cost in Sentry unless an error is captured.
   */
  breadcrumb: (message: string, data?: Record<string, any>) => {
    const enrichedData = enrich(data)
    if (isDev) console.log(`[ConnectStreams]`, message, enrichedData)
    Sentry.addBreadcrumb({
      category: module,
      message,
      level: 'info',
      data: enrichedData,
    })
  },
})

/**
 * Force any buffered Sentry logs to be sent immediately. Sentry batches logs and
 * flushes on a timer, so logs emitted right before the page goes away (tab close)
 * can be lost. Call this from a pagehide/visibilitychange handler after logging.
 * Fire-and-forget: the returned promise is intentionally not awaited by callers in
 * unload paths (fetch keepalive keeps the in-flight request alive).
 */
export const flushLogs = (): Promise<boolean> => {
  try {
    return Sentry.flush(2000)
  } catch {
    return Promise.resolve(false)
  }
}

/** CCP init/auth logger — filter in Sentry with module:ccp */
export const ccpLogger = createLogger('ccp')

/** Call lifecycle logger — filter in Sentry with module:calls */
export const callLogger = createLogger('calls')


/** Device-token / push-registration logger — filter in Sentry with module:token */
export const tokenLogger = createLogger('token')

/** Connect endpoint / network diagnostics — filter in Sentry with module:diagnostics */
export const diagnosticsLogger = createLogger('diagnostics')
